// The MBG fleet. Physics colliders are the contract; the Kiln models (art/kiln-workspace/assets) are
// fitted to them. The pickup's numbers are BUGGY/FUEL in tuning.ts, unchanged since Phase 1.
import { BUGGY, FUEL } from './tuning';

export type VehicleId = 'pickup' | 'motor' | 'truck';

export interface Mount {
  x: number;
  y: number;
  z: number;
  /** Uniform scale for the part on this vehicle (default 1). */
  s?: number;
}

export interface VehicleDef {
  id: VehicleId;
  name: string;
  blurb: string;
  /** Garage price in coins (0 = owned from the start). */
  price: number;
  chassis: { halfLength: number; halfHeight: number; halfDepth: number; density: number; friction: number; spawnClearance: number };
  /** Crash sensor (driver's helmet or cab roof), chassis space. */
  helmet: { radius: number; offset: { x: number; y: number } };
  wheel: { radius: number; halfWidth: number; frictionSlip: number; axleX: readonly [number, number]; axleY: number };
  suspension: {
    restLength: number;
    travel: number;
    stiffness: number;
    compression: number;
    relaxation: number;
    maxForce: number;
    bumpStopRadiusScale: number;
    bumpStopFriction: number;
  };
  drive: { acceleration: number; topSpeed: number; brake: number; reverseBelowSpeed: number; reverseTopSpeed: number };
  air: { pitchAccel: number; maxPitchRate: number };
  fuel: { capacity: number; drainIdle: number; drainThrottle: number };
  view: {
    body: string;
    wheel: string;
    /** Visual wheels sit at ±this along Z; 0 = a single-track vehicle (one wheel per axle). */
    wheelTrackZ: number;
    /** Painted "MBG" programme lettering on the camera side (centre, size), or null. */
    lettering: { x: number; y: number; z: number; w: number; h: number; ink: string } | null;
    /** Contact-shadow length in metres. */
    shadowLength: number;
    /** Tailpipe position for exhaust puffs, chassis space. */
    tailpipe: Mount;
    /** Where visible upgrades attach (null = that part isn't fitted on this vehicle). */
    mounts: { rack: Mount | null; scoop: Mount | null; stack: Mount | null; coilovers: boolean };
  };
  /** Synthesized engine character (src/audio/sound.ts). */
  sound: { base: number; span: number; bright: number };
}

export const PICKUP: VehicleDef = {
  id: 'pickup',
  name: 'Pickup MBG',
  blurb: 'The all-rounder. Steady, forgiving, room for a full load of meal trays.',
  price: 0,
  chassis: { ...BUGGY.chassis },
  helmet: { radius: BUGGY.helmet.radius, offset: { ...BUGGY.helmet.offset } },
  wheel: { ...BUGGY.wheel },
  suspension: { ...BUGGY.suspension },
  drive: { ...BUGGY.drive },
  air: { ...BUGGY.air },
  fuel: { capacity: FUEL.capacity, drainIdle: FUEL.drainIdle, drainThrottle: FUEL.drainThrottle },
  view: {
    body: 'mbg-car-body.glb',
    wheel: 'mbg-wheel.glb',
    wheelTrackZ: 0.42,
    // The box car's sky-blue side panel (art/reference/mobil-mbg.jpg): white lettering reads on it.
    lettering: { x: -0.59, y: 0.63, z: 0.633, w: 1.2, h: 0.6, ink: '#ffffff' },
    shadowLength: 3.2,
    tailpipe: { x: -1.38, y: -0.08, z: 0.35 },
    mounts: {
      rack: { x: -0.59, y: 1.13, z: 0 },
      scoop: { x: 0.98, y: 0.6, z: 0 },
      // In the gap between cab and box, just outside the body on the camera side.
      stack: { x: 0.14, y: 0.28, z: 0.66 },
      coilovers: true,
    },
  },
  sound: { base: 42, span: 120, bright: 520 },
};

export const MOTOR: VehicleDef = {
  id: 'motor',
  name: 'Motor Antar',
  blurb: 'Light courier bike. Flips fast and sips fuel, but the rider has no roof.',
  price: 400,
  chassis: { halfLength: 0.75, halfHeight: 0.18, halfDepth: 0.35, density: 290, friction: 0.6, spawnClearance: 0.85 },
  helmet: { radius: 0.22, offset: { x: -0.1, y: 0.95 } },
  wheel: { radius: 0.36, halfWidth: 0.06, frictionSlip: 6.5, axleX: [-0.62, 0.64], axleY: -0.36 },
  suspension: { restLength: 0.3, travel: 0.18, stiffness: 45, compression: 3.4, relaxation: 4.4, maxForce: 35000, bumpStopRadiusScale: 0.85, bumpStopFriction: 0.4 },
  drive: { acceleration: 7.2, topSpeed: 13.5, brake: 34, reverseBelowSpeed: 0.8, reverseTopSpeed: 3 },
  air: { pitchAccel: 12, maxPitchRate: 7.5 },
  fuel: { capacity: 70, drainIdle: 0.25, drainThrottle: 1.0 },
  view: {
    body: 'mbg-motor-body.glb',
    wheel: 'mbg-motor-wheel.glb',
    wheelTrackZ: 0,
    // The meal box's camera-side face (box centre x -0.72, y 0.33..0.79, face at z 0.23).
    lettering: { x: -0.72, y: 0.58, z: 0.236, w: 0.42, h: 0.3, ink: '#1f5fa8' },
    shadowLength: 2.0,
    tailpipe: { x: -0.85, y: -0.2, z: 0.14 },
    mounts: { rack: { x: -0.72, y: 0.835, z: 0, s: 0.55 }, scoop: null, stack: null, coilovers: false },
  },
  sound: { base: 70, span: 210, bright: 900 },
};

export const TRUCK: VehicleDef = {
  id: 'truck',
  name: 'Truk Dapur',
  blurb: 'The kitchen truck. Heavy, slow and very hard to flip, with a huge tank.',
  price: 900,
  chassis: { halfLength: 1.6, halfHeight: 0.38, halfDepth: 0.65, density: 130, friction: 0.6, spawnClearance: 1.2 },
  helmet: { radius: 0.3, offset: { x: 0.95, y: 1.05 } },
  wheel: { radius: 0.5, halfWidth: 0.15, frictionSlip: 7, axleX: [-1.15, 1.3], axleY: -0.62 },
  suspension: { restLength: 0.4, travel: 0.14, stiffness: 42, compression: 3.0, relaxation: 4.0, maxForce: 140000, bumpStopRadiusScale: 0.85, bumpStopFriction: 0.4 },
  drive: { acceleration: 5.4, topSpeed: 11, brake: 40, reverseBelowSpeed: 0.8, reverseTopSpeed: 3.5 },
  air: { pitchAccel: 6, maxPitchRate: 4.5 },
  fuel: { capacity: 160, drainIdle: 0.5, drainThrottle: 2.0 },
  view: {
    body: 'mbg-truck-body.glb',
    wheel: 'mbg-truck-wheel.glb',
    wheelTrackZ: 0.48,
    // The white field of the box side, between the red stripe and the top band, behind the hatch.
    lettering: { x: -0.85, y: 0.92, z: 0.68, w: 1.7, h: 0.5, ink: '#1f5fa8' },
    shadowLength: 4.2,
    tailpipe: { x: -1.7, y: -0.3, z: 0.5 },
    mounts: { rack: { x: -1.2, y: 1.36, z: 0 }, scoop: null, stack: { x: 0.52, y: 0.62, z: 0.6 }, coilovers: true },
  },
  sound: { base: 32, span: 85, bright: 380 },
};

export const VEHICLES: readonly VehicleDef[] = [PICKUP, MOTOR, TRUCK];

export function vehicleById(id: string): VehicleDef | undefined {
  return VEHICLES.find((v) => v.id === id);
}
